import { db } from '../lib/firebase';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';

export interface AdminMessage {
  id?: string;
  text: string;
  scope: 'all' | 'buyers' | 'sellers' | 'ids';
  targetUserIds?: string[];
  active: boolean;
  createdAt?: any; // Firestore Timestamp
}

export async function createAdminMessage(payload: { text: string; scope: AdminMessage['scope']; targetUserIds?: string[]; active?: boolean }) {
  const docRef = await addDoc(collection(db, 'admin_messages'), {
    text: payload.text,
    scope: payload.scope,
    targetUserIds: payload.scope === 'ids' ? (payload.targetUserIds || []) : [],
    active: payload.active !== false,
    createdAt: serverTimestamp(),
  } as AdminMessage);
  return docRef.id;
}

export async function listActiveMessagesForUser(uid: string, role: 'buyer' | 'seller' | 'admin' | null): Promise<AdminMessage[]> {
  const qRef = query(collection(db, 'admin_messages'), where('active', '==', true), orderBy('createdAt', 'desc'));
  const snap = await getDocs(qRef);
  const messages: AdminMessage[] = [];
  for (const d of snap.docs) {
    const data = d.data() as AdminMessage;
    // Skip dismissed messages for this user
    const dismissedSnap = await getDoc(doc(db, 'admin_messages', d.id, 'dismissals', uid));
    const dismissed = dismissedSnap.exists();
    if (dismissed) continue;
    const scopeOk = (
      data.scope === 'all' ||
      (data.scope === 'buyers' && role === 'buyer') ||
      (data.scope === 'sellers' && role === 'seller') ||
      (data.scope === 'ids' && Array.isArray(data.targetUserIds) && data.targetUserIds.includes(uid))
    );
    if (scopeOk) messages.push({ id: d.id, ...data });
  }
  return messages;
}

export async function dismissMessage(messageId: string, uid: string): Promise<void> {
  await setDoc(doc(db, 'admin_messages', messageId, 'dismissals', uid), { dismissedAt: serverTimestamp() }, { merge: true });
}

export function subscribeActiveMessagesForUser(uid: string, role: 'buyer' | 'seller' | 'admin' | null, callback: (msgs: AdminMessage[]) => void) {
  const qRef = query(collection(db, 'admin_messages'), where('active', '==', true), orderBy('createdAt', 'desc'));
  return onSnapshot(qRef, async (snap) => {
    const list: AdminMessage[] = [];
    for (const d of snap.docs) {
      const data = d.data() as AdminMessage;
      const dismissedSnap = await getDoc(doc(db, 'admin_messages', d.id, 'dismissals', uid));
      if (dismissedSnap.exists()) continue;
      const scopeOk = (
        data.scope === 'all' ||
        (data.scope === 'buyers' && role === 'buyer') ||
        (data.scope === 'sellers' && role === 'seller') ||
        (data.scope === 'ids' && Array.isArray(data.targetUserIds) && data.targetUserIds.includes(uid))
      );
      if (scopeOk) list.push({ id: d.id, ...data });
    }
    callback(list);
  });
}

export async function recordBuyerOnboardingResponse(uid: string, payload: { howKnowUs: string; intent: string; message?: string }) {
  const ref = await addDoc(collection(db, 'buyer_onboarding_responses'), {
    uid,
    howKnowUs: payload.howKnowUs,
    intent: payload.intent,
    message: payload.message || '',
    createdAt: serverTimestamp(),
  } as any);
  await setDoc(doc(db, 'users', uid), { buyerOnboardingDone: true }, { merge: true });
  return ref.id;
}